import type { EffortLevel, ModelEffortRule } from "./effort";

export type CredentialField = {
  key: string;
  label: string;
  description: string;
  placeholder: string;
  sensitive?: boolean;
  multiline?: boolean;
  required?: boolean;
};

export type PackedCredentials = {
  baseUrl: string;
  apiKey: string;
  caCertificate?: string;
};

export type AttachmentSupport = {
  documentMediaTypes: readonly string[];
  extensionToMediaType: Readonly<Record<string, string>>;
  modelMediaTypeOverrides?: Readonly<Record<string, string>>;
  // Media types listed here are inlined into the user message as text content
  // instead of being sent as document blocks. Every provider inherits the text
  // baseline below; document blocks are the per-provider opt-in for native
  // document understanding (PDF, Office files). Inlining is also required for
  // bedrock-anthropic because Bedrock rejects user messages that contain both
  // `toolResult` blocks and `document` blocks together (reported as a misleading
  // "tool_use ids without tool_result blocks" error against the prior assistant).
  inlineTextMediaTypes?: readonly string[];
};

// Text inlining needs no provider capability — the bytes become prompt text —
// so it's the universal baseline; a dropped text file is always a coverage gap.
const TEXT_INLINE_MEDIA_TYPES = [
  "text/plain",
  "text/csv",
  "text/html",
  "text/markdown",
  "application/json",
  "application/xml",
  "image/svg+xml",
] as const;

const TEXT_INLINE_EXTENSIONS: Readonly<Record<string, string>> = {
  txt: "text/plain",
  log: "text/plain",
  csv: "text/csv",
  html: "text/html",
  htm: "text/html",
  md: "text/markdown",
  markdown: "text/markdown",
  json: "application/json",
  xml: "application/xml",
  svg: "image/svg+xml",
};

type ProviderAttachmentSupport = {
  documentMediaTypes?: readonly string[];
  extensionToMediaType?: Readonly<Record<string, string>>;
  modelMediaTypeOverrides?: Readonly<Record<string, string>>;
  inlineTextMediaTypes?: readonly string[];
};

type AIProviderManifest<Id extends string = string> = {
  id: Id;
  label: string;
  description: string;
  // The endpoint the SDK hits when no base URL is set; scopes the proxy egress
  // route and connector match. Omitted for providers that require a base URL.
  defaultBaseUrl?: string;
  // The connector type offered for inline creation; omitted for providers with
  // no single-key connector (managed/bedrock, azure).
  connectorIdentifier?: string;
  // When set, the connector owns the endpoint (its `configuration.baseUrl`), so
  // the AI-provider modal hides its own Base URL field and derives the row's
  // baseUrl from the selected connector at save.
  endpointFromConnector?: boolean;
  defaultChatModel: string;
  defaultFastModel: string;
  defaultChatEffort?: EffortLevel;
  modelEfforts?: readonly ModelEffortRule[];
  configurable: boolean;
  requiresChatModel: boolean;
  credentialFields: readonly CredentialField[];
  packCredentials: (values: Record<string, string>) => PackedCredentials;
  unpackCredentials: (row: PackedCredentials) => Record<string, string>;
  subtitleField: string | null;
  attachmentSupport: AttachmentSupport;
  // The `providerOptions` key under which a persisted reasoning signature is
  // sent back to the model (e.g. `anthropic`/`bedrock`). Set only for providers
  // whose thinking blocks must round-trip a signature; serialization stamps the
  // signature directly onto each reasoning part so it can never desync from its
  // text.
  reasoningSignatureProviderOptionsKey?: string;
};

type AIProviderManifestInput<Id extends string> = Omit<
  AIProviderManifest<Id>,
  "attachmentSupport"
> & {
  attachmentSupport?: ProviderAttachmentSupport;
};

export function defineAIProviderManifest<const Id extends string>(
  manifest: AIProviderManifestInput<Id>
): AIProviderManifest<Id> {
  const native = manifest.attachmentSupport;
  return {
    ...manifest,
    attachmentSupport: {
      documentMediaTypes: native?.documentMediaTypes ?? [],
      extensionToMediaType: {
        ...TEXT_INLINE_EXTENSIONS,
        ...native?.extensionToMediaType,
      },
      inlineTextMediaTypes: [
        ...TEXT_INLINE_MEDIA_TYPES,
        ...(native?.inlineTextMediaTypes ?? []),
      ],
      modelMediaTypeOverrides: native?.modelMediaTypeOverrides,
    },
  };
}

export function indexAIProvidersById<
  const Providers extends readonly AIProviderManifest[],
>(providers: Providers) {
  return Object.fromEntries(
    providers.map((provider) => [provider.id, provider])
  ) as {
    [Provider in Providers[number] as Provider["id"]]: Provider;
  };
}

export function emptyCredentials(
  fields: readonly CredentialField[]
): Record<string, string> {
  return Object.fromEntries(fields.map((field) => [field.key, ""]));
}
